import { setEvaluation, setRelevance as setRelevant } from "@/services/statement.service";

type OpType = "evaluation" | "relevant";

interface Mutation {
  id: string;                 // uuid-ish
  ts: number;                 // insertion timestamp
  type: OpType;
  templateId: string;
  statementId: string;
  payload: any;               // { evaluation } | { relevant }
  attempt: number;            // retries so far
  nextAt: number;             // eligible time to retry (ms epoch)
}

const LS_KEY = "xyb.mq.v1";
let queue: Mutation[] = load();
let running = false;
let timer: number | null = null;

function uuid() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
function now() { return Date.now(); }
function jitter(n: number) { return n + Math.floor(Math.random() * 300); } // up to 300ms
function backoffMs(attempt: number) {
  const base = 500;                  // 0.5s
  const capped = Math.min(base * 2 ** attempt, 60_000); // cap 60s
  return jitter(capped);
}
function save() {
  try { localStorage.setItem(LS_KEY, JSON.stringify(queue)); } catch { /* ignore */ }
}
function load(): Mutation[] {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]") as Mutation[]; } catch { return []; }
}
function scheduleNextTick() {
  if (timer) window.clearTimeout(timer);
  const next = queue.reduce<number>((acc, m) => Math.min(acc, m.nextAt), Infinity);
  const delay = Math.max(0, (isFinite(next) ? next : now()) - now());
  timer = window.setTimeout(flush, delay || 0);
}

async function run(m: Mutation): Promise<void> {
  if (m.type === "evaluation") {
    await setEvaluation(m.templateId, m.statementId, m.payload.evaluation);
  } else {
    await setRelevant(m.templateId, m.statementId, m.payload.relevant);
  }
}

function shouldDrop(status: number | undefined) {
  // Drop 4xx (except 429) as non-retryable. Retry on network errors and 5xx/429.
  if (status == null) return false;
  if (status === 429) return false;
  return status >= 400 && status < 500;
}

async function flush() {
  if (running) return;
  running = true;

  try {
    // process in chronological order, but only those whose nextAt has passed
    for (let i = 0; i < queue.length; ) {
      const m = queue[i];
      if (m.nextAt > now()) { i++; continue; }

      try {
        await run(m);
        queue.splice(i, 1);
        save();
        // continue without incrementing i
      } catch (e: any) {
        const status = e?.response?.status ?? e?.status;
        if (shouldDrop(status)) {
          // Remove unretryable
          queue.splice(i, 1);
          save();
          continue;
        }
        // Retry later
        m.attempt += 1;
        m.nextAt = now() + backoffMs(m.attempt);
        save();
        i++;
      }
    }
  } finally {
    running = false;
    scheduleNextTick();
  }
}

function coalesce(type: OpType, templateId: string, statementId: string) {
  // Remove any older mutation of the same type & key; keep ordering across different types
  const key = `${type}:${templateId}:${statementId}`;
  for (let i = queue.length - 1; i >= 0; i--) {
    const q = queue[i];
    if (`${q.type}:${q.templateId}:${q.statementId}` === key) queue.splice(i, 1);
  }
}

export function enqueueEvaluation(templateId: string, statementId: string, evaluation: number) {
  coalesce("evaluation", templateId, statementId);
  queue.push({
    id: uuid(),
    ts: now(),
    type: "evaluation",
    templateId, statementId,
    payload: { evaluation },
    attempt: 0,
    nextAt: now()
  });
  save();
  scheduleNextTick();
}

export function enqueueRelevant(templateId: string, statementId: string, relevant: boolean) {
  coalesce("relevant", templateId, statementId);
  queue.push({
    id: uuid(),
    ts: now(),
    type: "relevant",
    templateId, statementId,
    payload: { relevant },
    attempt: 0,
    nextAt: now()
  });
  save();
  scheduleNextTick();
}

export function startQueue() {
  // Flush immediately and on key lifecycle events
  flush();
  window.addEventListener("online", flush);
  window.addEventListener("focus", flush);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") flush(); });
}

export function stopQueue() {
  window.removeEventListener("online", flush);
  window.removeEventListener("focus", flush);
}
